// The audible half of the game's tuning, kept pure so it can be tested.
//
// audio.ts owns the Web Audio graph and all of its side effects; everything
// here is arithmetic. The split is what lets the mix be unit-tested at all:
// a suspended AudioContext cannot be asserted against, but the curve that
// feeds it can.
import { COURSE_WIDTH, GRAZE_MIN_SPEED, MAX_SPEED } from "./constants";

export function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

// ---------------------------------------------------------------------------
// Tonality. Every pitched sound in the game sits in C major pentatonic so any
// overlap -- a pickup ringing into the finish line, two foods in one substep
// -- stays consonant. Just-intonation ratios, because synth partials beat
// audibly against tempered thirds.
const PENTATONIC = [1, 9 / 8, 5 / 4, 3 / 2, 5 / 3];
export const LADDER_BASE_HZ = 523.25; // C5
export const LADDER_CAP = 7; // one octave above base, in pentatonic degrees
export const LADDER_RESET_S = 4;

export function pentatonicHz(step: number, baseHz = LADDER_BASE_HZ): number {
  const s = Math.max(0, Math.round(step));
  const octave = Math.floor(s / PENTATONIC.length);
  return baseHz * PENTATONIC[s % PENTATONIC.length]! * 2 ** octave;
}

// The pickup ladder: successive pickups climb one degree, cap an octave up,
// and fall back to the root after a quiet spell or a crash. The ladder is the
// streak made audible -- rising pitch reads as "keep going" without a single
// UI element.
export type Ladder = { idx: number; lastAt: number };

export function ladderStart(): Ladder {
  return { idx: 0, lastAt: -Infinity };
}

export function ladderAdvance(l: Ladder, now: number): { play: number; next: Ladder } {
  const expired = now - l.lastAt > LADDER_RESET_S;
  const play = expired ? 0 : Math.min(l.idx, LADDER_CAP);
  return { play, next: { idx: Math.min(play + 1, LADDER_CAP), lastAt: now } };
}

// A hot dog is two quick pentatonic notes; a burger -- three times the bonus
// -- is a four-note spread with an extra degree of reach. Both are built from
// the ladder step so a streak keeps climbing whichever food lands.
export function hotDogNotes(step: number): number[] {
  return [pentatonicHz(step), pentatonicHz(step + 2)];
}

export function burgerNotes(step: number): number[] {
  return [
    pentatonicHz(step),
    pentatonicHz(step + 1),
    pentatonicHz(step + 2),
    pentatonicHz(step + 4),
  ];
}

// ---------------------------------------------------------------------------
// The ambience mix, as one pure function of game state. audio.ts applies
// these targets with slew; nothing here touches a node.
export type AmbienceState = {
  speed01: number;
  grounded: boolean;
  lean: number;
  stunned: boolean;
  idle: boolean;
};

export type AmbienceTargets = {
  ear: { gain: number; lowHz: number; bandHz: number };
  slide: { gain: number; bandHz: number };
  rumble: number;
  carve: { gain: number; bandHz: number };
  whistle: number;
  bed: number;
  lowpassHz: number;
};

export function ambienceTargets(s: AmbienceState): AmbienceTargets {
  const v = clamp01(s.speed01);
  const airborne = !s.grounded && !s.idle;
  const contact = s.grounded && !s.idle;
  // A stun ducks the whole bed and pulls the lowpass down: snow is dead air,
  // and getting your bell rung should sound like it.
  const stun = s.stunned ? 0.6 : 1;

  // Aerodynamic noise grows superlinearly with speed; the ^1.7 keeps a slow
  // creep near-silent so a full-tilt run has somewhere to go. Airborne, the
  // wind blooms -- nothing between the ears and the air.
  const bloom = airborne ? 1.4 : 1;
  const ear = {
    gain: (0.02 + 0.24 * v ** 1.7) * bloom * stun,
    lowHz: (250 + 1550 * v) * (airborne ? 1.5 : 1),
    bandHz: (360 + 900 * v) * (airborne ? 1.5 : 1),
  };

  // Contact noise exists even at crawl speed, unlike wind -- the floor term
  // is what says "on the snow" the moment a run starts.
  const slide = {
    gain: contact ? (0.025 + 0.115 * v ** 0.7) * stun : 0,
    bandHz: 1200 + 1000 * v,
  };
  const rumble = contact ? 0.05 * v * stun : 0;

  // The edge rasp: louder and brighter the harder the carve, scaled by speed
  // so parking-lot wiggles stay silent.
  const lean = Math.min(1, Math.abs(s.lean));
  const carve = {
    gain: contact ? 0.12 * lean * v * stun : 0,
    bandHz: 2800 + 1200 * lean,
  };

  return {
    ear,
    slide,
    rumble,
    carve,
    whistle: airborne ? 0.035 : 0,
    bed: 0.03 * stun,
    lowpassHz: s.stunned ? 600 : 18000,
  };
}

// ---------------------------------------------------------------------------
// One-shot scaling.

// Touchdown speed maps to how hard the whump lands. A ramp hop touches down
// around 250-330 px/s; anything under the floor reads as a settle, not a hit.
export function landingImpact01(fallSpeed: number): number {
  return clamp01((fallSpeed - 180) / 160);
}

export function landingPeak(impact01: number): number {
  return 0.1 + 0.15 * clamp01(impact01);
}

// Course x to stereo pan. +-0.4 keeps it a lean, not a hard split -- phone
// speakers collapse to mono and full-width pans just lose energy there.
export function panForCourseX(x: number): number {
  return Math.max(-1, Math.min(1, (x / COURSE_WIDTH) * 2 - 1)) * 0.4;
}

// A pile-up should not double-punish: crashes inside three seconds of the
// last arrive softened.
export function crashLevelScale(sinceLastS: number): number {
  return sinceLastS < 3 ? 0.78 : 1;
}

export function grazeIntensity(speedY: number): number {
  return clamp01((speedY - GRAZE_MIN_SPEED) / (MAX_SPEED - GRAZE_MIN_SPEED));
}

// ---------------------------------------------------------------------------
// Sidechain. Every one-shot dips the music bus for its own duration, so the
// score gets out of the way of the game rather than the other way round. The
// amounts are here, with the rest of the mix's arithmetic, so the relative
// weights can be read and tested in one place.

export const DUCK_ATTACK_S = 0.015;

// Never all the way to silence: a bus snapped to zero and back is a hole in
// the music, which is more noticeable than the sound that caused it.
export const DUCK_FLOOR_MIN = 0.05;

export type DuckShape = { amount: number; hold: number; release: number };

export const DUCKS = {
  start: { amount: 0.3, hold: 0.05, release: 0.2 },
  // Deepest by a distance. A crash already buys silence from the ambience bed;
  // the score has to leave too or the impact lands in the middle of a groove.
  crash: { amount: 0.62, hold: 0.09, release: 0.28 },
  jump: { amount: 0.2, hold: 0.01, release: 0.12 },
  // Pickups are the most frequent reward and the one the ladder is built on,
  // so they get more room than their level alone would ask for.
  collect: { amount: 0.42, hold: 0.02, release: 0.16 },
  nearMiss: { amount: 0.18, hold: 0.02, release: 0.14 },
  finish: { amount: 0.45, hold: 0.5, release: 0.5 },
  posted: { amount: 0.2, hold: 0.02, release: 0.15 },
} as const satisfies Record<string, DuckShape>;

export function landingDuck(impact01: number): DuckShape {
  return { amount: 0.22 + 0.18 * clamp01(impact01), hold: 0.02, release: 0.14 };
}

export type DuckState = { floor: number; until: number };

export function duckIdle(): DuckState {
  return { floor: 1, until: -Infinity };
}

// Which duck wins when two overlap. A quieter, shorter cue arriving inside a
// deeper one is dropped, so a pickup caught mid-crash cannot lift the music
// back up through the silence the crash just bought. Null means "ignore it".
export function duckPlan(current: DuckState, now: number, shape: DuckShape): DuckState | null {
  const floor = Math.max(DUCK_FLOOR_MIN, 1 - shape.amount);
  const until = now + DUCK_ATTACK_S + shape.hold + shape.release * 3;
  if (now < current.until && floor >= current.floor && until <= current.until) return null;
  return { floor, until };
}

// Same-sound retrigger gate; simultaneous triggers escalate the ladder or
// stack physics, they do not stack identical waveforms.
export function retriggerOk(lastAt: number, now: number, minGapS: number): boolean {
  return now - lastAt >= minGapS;
}
